import {
  create_requestor_case,
  fetch_requestor_application_forms,
  first_truthy,
  submit_requestor_form_answers,
  to_number,
} from './request_portal_helpers';

function new_case_error_message(error) {
  return friendly_new_case_error_message(first_truthy([
    error?.response?.data?.message,
    error?.response?.data?.data?.message,
    error?.message,
    'Failed to open the case.',
  ]));
}

function friendly_new_case_error_message(message) {
  const normalized_message = String(message || '').trim();
  const lower_message = normalized_message.toLowerCase();

  if (
    lower_message.includes('no workflow status has been configured')
    || lower_message.includes('no workflow statuses is configured')
  ) {
    return 'This form cannot be submitted yet because no workflow status has been configured for it.';
  }

  return normalized_message || 'Failed to open the case.';
}

function response_entity_profile_id(response = {}) {
  return to_number(first_case_response_value([
    response?.entity_profile_id,
    response?.data?.entity_profile_id,
    response?.entity_id,
    response?.data?.entity_id,
  ]));
}

function response_application_id(response = {}) {
  return to_number(first_case_response_value([response?.application_id, response?.data?.application_id]));
}

function selected_form_ids(context) {
  const case_type_form_ids = Array.isArray(context.selected_case_type?.form_ids)
    ? context.selected_case_type.form_ids
    : [];
  const resolved_form_ids = case_type_form_ids.length ? case_type_form_ids : [context.selected_form_id];
  return resolved_form_ids.map((form_id) => Number(form_id)).filter(Boolean);
}

function has_error_status(response = {}) {
  return String(response?.status || '').toLowerCase() === 'error';
}

function new_case_payload(context, submitted_answers = []) {
  return {
    entity_name: context.build_case_entity_name(submitted_answers),
    entity_type_id: Number(context.selected_entity_type_id),
    ...(context.project_id ? { project_id: Number(context.project_id) } : {}),
    form_ids: selected_form_ids(context),
  };
}

function validate_new_case(context) {
  if (!context.selected_entity_type_id || !selected_form_ids(context).length || !context.section_cards.length) {
    context.modal_error = 'Select a case type with an active form before submitting.';
    context.scroll_new_case_feedback_into_view();
    return false;
  }

  if (!context.has_any_answered_questions) {
    context.modal_error = 'Complete the form before opening the case.';
    context.validation_errors = [];
    context.scroll_new_case_feedback_into_view();
    return false;
  }

  return context.validate_dynamic_form();
}

function handle_case_response_error(context, response = {}) {
  if (has_error_status(response)) {
    context.modal_error = friendly_new_case_error_message(response?.message || 'Failed to open the case.');
    context.scroll_new_case_feedback_into_view();
  }
}

function first_case_response_value(values = []) {
  return values.find(Boolean);
}

async function runtime_application_form(context, application_id) {
  const application_forms = await fetch_requestor_application_forms(application_id);
  return selected_form_ids(context)
    .map((form_id) => context.application_form_for_form_id(application_forms, form_id))
    .filter(Boolean);
}

async function submit_new_case_answers(context, response = {}, submitted_answers = []) {
  const application_id = response_application_id(response);
  if (!application_id) return [];

  const application_forms = await runtime_application_form(context, application_id);
  if (!application_forms.length) return [];

  await Promise.all(
    application_forms.map((application_form) =>
      submit_runtime_answers(context, application_form, submitted_answers, application_id)
    )
  );
  return application_forms;
}

async function submit_new_case_risk_answers(context, response = {}, application_forms = [], submitted_answers = []) {
  const [application_form] = application_forms;
  await context.submit_case_risk_answers({
    entity_profile_id: first_case_response_value([
      response_entity_profile_id(response),
      application_form?.entity_profile_id,
      application_form?.entity_id,
    ]),
    submitted_answers,
  });
}

async function complete_new_case_success(context) {
  await context.load_open_cases({ force: true });
  context.active_tab = 'active';
  context.close_new_case_modal();
}

function runtime_application_form_application_id(runtime_application_form = {}) {
  return to_number(first_truthy([
    runtime_application_form?.application_id,
    runtime_application_form?.application?.id,
    runtime_application_form?.entity_application?.id,
  ]));
}

function assert_runtime_application_form_matches(application_form, application_id) {
  const runtime_application_id = runtime_application_form_application_id(application_form);
  if (runtime_application_id && Number(runtime_application_id) !== Number(application_id)) {
    throw new Error('The submitted form belongs to another case. Please reopen the form and try again.');
  }
}

async function submit_runtime_answers(context, runtime_application_form, submitted_answers, application_id) {
  assert_runtime_application_form_matches(runtime_application_form, application_id);
  const runtime_questions = context.flatten_application_form_questions(runtime_application_form);
  const answers_payload = context.build_answer_submission_payload(runtime_questions, submitted_answers);

  if (!answers_payload.length) {
    return;
  }

  const section_count = Array.isArray(runtime_application_form?.form?.sections)
    ? runtime_application_form.form.sections.length
    : 0;

  await submit_requestor_form_answers({
    application_form_id: Number(runtime_application_form.application_form_id),
    application_id: Number(application_id),
    current_section_index: Math.max(section_count - 1, 0),
    answers: answers_payload,
  });
}

export default {
    submit_new_case() {
      if (!validate_new_case(this)) return;

      return submit_new_case_for_context(this);
    },
};

async function submit_new_case_for_context(context) {
  context.submitting_new_case = true;
  context.modal_error = '';

  try {
    await submit_new_case_flow(context);
  } catch (error) {
    context.modal_error = new_case_error_message(error);
    context.scroll_new_case_feedback_into_view();
  } finally {
    context.submitting_new_case = false;
  }
}

async function submit_new_case_flow(context) {
  const submitted_answers = context.build_submission_answers();
  const response = await create_requestor_case(new_case_payload(context, submitted_answers));
  handle_case_response_error(context, response);
  const application_forms = await submit_new_case_answers(context, response, submitted_answers);
  if (!application_forms.length) return;
  await submit_new_case_risk_answers(context, response, application_forms, submitted_answers);
  await complete_new_case_success(context);
}
